import {
  AppBar,
  Paper,
  CircularProgress,
  Drawer,
  IconButton,
  ListItem,
  ListItemText,
  Toolbar,
  Typography,
  useMediaQuery,
  Box,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ViewList as ViewListIcon,
} from '@mui/icons-material';
import { Fragment, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import _ from 'lodash';
import { FETCH_REPLAY } from '../../actions';
import ReplayControls from './ReplayControls';
import ReplayItemList from './ReplayItemList';
import ReplayMap from './ReplayMap';

export default function Replay() {
  const navigate = useNavigate();
  const { id, type } = useParams();
  const dispatch = useDispatch();
  const frames = useSelector((state) => state.replay.frames, _.isEqual);
  const paths = useSelector((state) => state.replay.paths, _.isEqual);
  const frameBySecond = useSelector((state) => state.replay.frameBySecond);
  const [selectedItemIndex, setSelectedItemIndex] = useState({});
  const [hoveredItemIndex, setHoveredItemIndex] = useState({});
  const [currentFrameIndex, setCurrentFrameIndex] = useState(0);
  const [followedItemIndexes, setFollowedItemIndexes] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isXs = useMediaQuery((theme) => theme.breakpoints.only('xs'));

  useEffect(() => {
    dispatch({
      type: FETCH_REPLAY,
      payload: { type, id },
    });
  }, [type, id, dispatch]);

  useEffect(() => {
    if (Number.isInteger(selectedItemIndex)) {
      setDrawerOpen(true);
    }
  }, [selectedItemIndex]);

  function handleOpen() {
    setDrawerOpen(true);
  }

  function handleClose() {
    setDrawerOpen(false);
  }

  function handleBackClick() {
    navigate(-1);
  }

  const isLoading = useSelector((state) => state.replay.isLoading);

  function showLoader() {
    return (
      <Box sx={{ px: 1, py: 4, textAlign: 'center' }}>
        <CircularProgress size={80} thickness={6} />
        <Typography>Fetching replay...</Typography>
      </Box>
    );
  }

  function renderReplayMap() {
    return (
      <ReplayMap
        paths={paths}
        featureCollections={
          frames.length > 0
            ? frames[currentFrameIndex].featureCollections
            : null
        }
        followedItemIndexes={followedItemIndexes}
        selectedItemIndex={selectedItemIndex}
        hoveredItemIndex={hoveredItemIndex}
        onSelectItem={setSelectedItemIndex}
        onHoverItem={setHoveredItemIndex}
      />
    );
  }

  function renderReplayControls() {
    return (
      <ReplayControls
        // time={
        //   frames.length > 0
        //     ? frames[currentFrameIndex].time
        //     : new Date('1900-1-1')
        // }
        // frameCount={frames.length}
        // currentFrameIndex={currentFrameIndex}
        // onFrameChange={setCurrentFrameIndex}

        frames={frames}
        start={_.first(frames)?.time}
        end={_.last(frames)?.time}
        onTick={(seconds) => {
          setCurrentFrameIndex(frameBySecond[seconds] ?? frames.length - 1);
        }}
      />
    );
  }

  function renderReplayItemList() {
    return (
      <ReplayItemList
        featureCollections={
          frames.length > 0
            ? frames[currentFrameIndex].featureCollections
            : null
        }
        followedItemIndexes={followedItemIndexes}
        selectedItemIndex={selectedItemIndex}
        hoveredItemIndex={hoveredItemIndex}
        onFollowedItemsChange={setFollowedItemIndexes}
        onSelectItem={setSelectedItemIndex}
        onHoverItem={setHoveredItemIndex}
      />
    );
  }

  function mapSection() {
    if (isLoading) {
      return (
        <Paper
          sx={{
            height: 'calc(100vh - 125px)',
            m: 1,
            mt: 0,
            minWidth: 250,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {showLoader()}
        </Paper>
      );
    } else if (isXs) {
      return (
        <Fragment>
          {renderReplayControls()}
          <Box sx={{ height: 'calc(100% - 45px)' }}>{renderReplayMap()}</Box>
        </Fragment>
      );
    } else {
      return (
        <Fragment>
          <Paper
            sx={{
              height: 'calc(100vh - 125px)',
              m: 1,
              mt: 0,
              minWidth: 250,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            {renderReplayMap()}
          </Paper>
          {renderReplayControls()}
        </Fragment>
      );
    }
  }

  return (
    <Fragment>
      <Helmet>
        <title>IR3 | Replay | {id}</title>
      </Helmet>
      <AppBar position="static">
        <Toolbar variant="dense">
          <ListItem sx={{ p: 0 }} component="div">
            <IconButton
              color="inherit"
              aria-label="Back"
              sx={{ ml: -1.5, mr: 1.25 }}
              onClick={handleBackClick}
              size="large"
            >
              <ArrowBackIcon />
            </IconButton>
            <ListItemText
              sx={{ p: 0 }}
              primaryTypographyProps={{ color: 'inherit', noWrap: true }}
              secondaryTypographyProps={{ color: 'inherit', noWrap: true }}
              primary={id || ''}
              // secondary={replay ? replay.identifier : ''}
            />
            {isXs && (
              <IconButton
                color="inherit"
                aria-label="Details"
                sx={{ mr: 0 }}
                onClick={handleOpen}
                size="large"
              >
                <ViewListIcon />
              </IconButton>
            )}
          </ListItem>
        </Toolbar>
      </AppBar>
      <Box sx={{ mt: 1, display: 'flex' }}>
        {!isXs && (
          <Box
            sx={{
              width: 300,
              height: 'calc(100vh - 48px)',
              overflowY: 'auto',
              overflowX: 'hidden',
            }}
          >
            {id ? renderReplayItemList() : showLoader()}
          </Box>
        )}
        <Box
          sx={(theme) => ({
            width: 'calc(100% - 300px)',
            [theme.breakpoints.down('sm')]: {
              width: 1,
            },
            height: 'calc(100vh - 48px)',
            overflowY: 'auto',
            overflowX: 'hidden',
          })}
        >
          {mapSection()}
        </Box>
      </Box>
      {isXs && (
        <Drawer anchor="bottom" open={drawerOpen} onClose={handleClose}>
          <Box sx={{ maxHeight: '80vh', overflow: 'scroll' }}>
            {id ? renderReplayItemList() : showLoader()}
          </Box>
        </Drawer>
      )}
    </Fragment>
  );
}
